import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { MapPin, RefreshCw, X } from 'lucide-react';
import SortableGalleryGrid from '@/pages/admin/SortableGalleryGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EntityModal = ({
  open,
  onOpenChange,
  editingEntity,
  entityForm,
  setEntityForm,
  entityTypes,
  existingBusinessGroups,
  galleryImages,
  profileImagePreview,
  uploadingFile,
  onFileUpload,
  onGalleryUploadMultiple,
  onRemoveGalleryImage,
  onReorderGalleryImages,
  onSave,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingLocation, setSearchingLocation] = useState(false);
  const debounceRef = useRef(null);
  const lastSelectedRef = useRef('');

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const onLocationChange = (value) => {
    setEntityForm({ ...entityForm, location: value });
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Skip autocomplete for "lat, lng" pairs or values already chosen as suggestion.
    const looksLikeCoords = /^\s*-?\d{1,3}(?:\.\d+)?\s*,\s*-?\d{1,3}(?:\.\d+)?\s*$/.test(value);
    if (!value || value.length < 3 || looksLikeCoords || value === lastSelectedRef.current) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearchingLocation(true);
      try {
        const res = await axios.get(`${API}/geocode`, {
          params: { q: value, limit: 5 },
        });
        setSuggestions(res.data?.results || []);
        setShowSuggestions(true);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setSearchingLocation(false);
      }
    }, 350);
  };

  const onSelectSuggestion = (s) => {
    lastSelectedRef.current = s.label;
    setEntityForm({
      ...entityForm,
      location: s.label,
      latitude: s.lat,
      longitude: s.lng,
    });
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingEntity ? 'Edit Partner' : 'Create New Partner'}</DialogTitle>
          <DialogDescription>
            {editingEntity ? 'Update the entity details below' : 'Fill in the details to create a new entity'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={entityForm.name}
                onChange={(e) => setEntityForm({ ...entityForm, name: e.target.value })}
                placeholder="Partner name"
                data-testid="entity-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="entity_type">Type *</Label>
              <Select
                value={entityForm.entity_type}
                onValueChange={(v) => setEntityForm({ ...entityForm, entity_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {entityTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={entityForm.description}
              onChange={(e) => setEntityForm({ ...entityForm, description: e.target.value })}
              placeholder="Enter description..."
              rows={3}
              data-testid="entity-description-input"
            />
          </div>

          {/* Business / Organization — free-text used to group multi-location
              partners together (e.g. "KOA", "Marriott"). Optional. Locations
              with an identical value collapse under one parent row in the
              admin table and show a "Part of X" badge on public cards. */}
          <div className="space-y-2">
            <Label htmlFor="business_group">Business / Organization</Label>
            <Input
              id="business_group"
              value={entityForm.business_group || ''}
              onChange={(e) => setEntityForm({ ...entityForm, business_group: e.target.value })}
              placeholder="e.g. KOA, Marriott (leave blank for standalone)"
              data-testid="entity-business-group-input"
              list="business-group-suggestions"
            />
            {/* Native datalist auto-suggests existing group names — populated by
                the parent from the current entities list. No backend call. */}
            <datalist id="business-group-suggestions">
              {(existingBusinessGroups || []).map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 relative">
              <Label htmlFor="location">Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="location"
                  value={entityForm.location || ''}
                  onChange={(e) => onLocationChange(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Full address or lat, lng"
                  className="pl-9"
                  data-testid="entity-location-input"
                  autoComplete="off"
                />
                {searchingLocation && (
                  <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
                )}
              </div>
              {showSuggestions && suggestions.length > 0 && (
                <div
                  className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
                  data-testid="location-suggestions"
                >
                  {suggestions.map((s, idx) => (
                    <button
                      key={`${s.lat}-${s.lng}-${idx}`}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onSelectSuggestion(s)}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors border-b border-border last:border-b-0"
                      data-testid={`location-suggestion-${idx}`}
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                        <span className="line-clamp-2">{s.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Enter a full address (auto-geocoded) or coordinates as <code>lat, lng</code>.
              </p>
              {(entityForm.latitude !== null && entityForm.latitude !== undefined && entityForm.latitude !== '') && (
                <p className="text-xs text-muted-foreground" data-testid="entity-coords-display">
                  Pin: {Number(entityForm.latitude).toFixed(4)}, {Number(entityForm.longitude).toFixed(4)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="image_url">Profile Image/Video</Label>
              <div className="space-y-3">
                <Input
                  id="image_url"
                  value={entityForm.image_url}
                  onChange={(e) => setEntityForm({ ...entityForm, image_url: e.target.value })}
                  placeholder="Or paste image/video URL"
                />
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => e.target.files[0] && onFileUpload(e.target.files[0], 'profile')}
                    disabled={uploadingFile}
                    className="flex-1"
                  />
                  {uploadingFile && <RefreshCw className="h-4 w-4 animate-spin" />}
                </div>
                {(entityForm.image_url || profileImagePreview) && (
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-border">
                    {(entityForm.image_url?.includes('.mp4') || entityForm.image_url?.includes('.mov') || entityForm.image_url?.includes('.webm')) ? (
                      <video src={entityForm.image_url || profileImagePreview} className="w-full h-full object-cover" controls />
                    ) : (
                      <img src={entityForm.image_url || profileImagePreview} alt="Preview" className="w-full h-full object-cover" />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Gallery Images (Max 10)</Label>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      onGalleryUploadMultiple(e.target.files);
                    }
                    // Reset the input so re-selecting the same files re-fires onChange.
                    e.target.value = '';
                  }}
                  disabled={uploadingFile || galleryImages.length >= 10}
                  className="flex-1"
                  data-testid="gallery-multi-file-input"
                />
                {uploadingFile && <RefreshCw className="h-4 w-4 animate-spin" />}
              </div>
              <p className="text-xs text-muted-foreground">
                {galleryImages.length}/10 images uploaded
                {galleryImages.length < 10 && ` · select up to ${10 - galleryImages.length} more at once`}
              </p>
              {galleryImages.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Drag thumbnails to reorder — the first image is used as the partner&apos;s hero.
                </p>
              )}
              <SortableGalleryGrid
                images={galleryImages}
                onRemove={onRemoveGalleryImage}
                onReorder={onReorderGalleryImages}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_email">Contact Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={entityForm.contact_email}
                onChange={(e) => setEntityForm({ ...entityForm, contact_email: e.target.value })}
                placeholder="contact@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={entityForm.website}
                onChange={(e) => setEntityForm({ ...entityForm, website: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags (comma-separated)</Label>
            <Input
              id="tags"
              value={entityForm.tags}
              onChange={(e) => setEntityForm({ ...entityForm, tags: e.target.value })}
              placeholder="hiking, beginner-friendly, family"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="specialties">Specialties (comma-separated)</Label>
              <Input
                id="specialties"
                value={entityForm.specialties}
                onChange={(e) => setEntityForm({ ...entityForm, specialties: e.target.value })}
                placeholder="hiking, camping, kayaking"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="years_experience">Years Experience</Label>
              <Input
                id="years_experience"
                type="number"
                value={entityForm.years_experience}
                onChange={(e) => setEntityForm({ ...entityForm, years_experience: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="certifications">Certifications (comma-separated)</Label>
            <Input
              id="certifications"
              value={entityForm.certifications}
              onChange={(e) => setEntityForm({ ...entityForm, certifications: e.target.value })}
              placeholder="Wilderness First Responder, CPR Certified"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="image_gallery">Image Gallery (comma-separated URLs)</Label>
            <Textarea
              id="image_gallery"
              value={entityForm.image_gallery}
              onChange={(e) => setEntityForm({ ...entityForm, image_gallery: e.target.value })}
              placeholder="https://image1.jpg, https://image2.jpg"
              rows={2}
            />
            <p className="text-xs text-muted-foreground">Add multiple images for verified profiles</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="promo_codes">Promotional Codes (JSON format)</Label>
            <Textarea
              id="promo_codes"
              value={entityForm.promo_codes}
              onChange={(e) => setEntityForm({ ...entityForm, promo_codes: e.target.value })}
              placeholder='[{"code":"SUMMER20","description":"Summer discount","discount":"20% off","expiry_date":"2026-08-31","affiliate_link":"https://..."}]'
              rows={5}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">Optional: Add "affiliate_link" for external redemption URLs</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="offers">Special Offers (JSON format)</Label>
            <Textarea
              id="offers"
              value={entityForm.offers}
              onChange={(e) => setEntityForm({ ...entityForm, offers: e.target.value })}
              placeholder='[{"title":"Spring Package","description":"Complete outdoor package","valid_until":"2026-05-31","affiliate_link":"https://..."}]'
              rows={5}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">Optional: Add "affiliate_link" for booking/purchase URLs</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} data-testid="save-entity-btn">
            {editingEntity ? 'Save Changes' : 'Create Partner'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EntityModal;
